import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TbFileCheck } from "react-icons/tb";
import { PiBookBookmarkBold } from "react-icons/pi";
import { LuCalendar } from "react-icons/lu";
import { FiExternalLink, FiEdit3 } from "react-icons/fi";
import { IoMdHelpBuoy } from "react-icons/io";
import { FaChartLine } from "react-icons/fa";
import CustomButton from "../../components/CustomButton";
import NoticeTag from "../../components/ui/tags/NoticeTag";
import { VoteButton } from "../../components/buttons/VoteButton";
import { FiSearch } from "react-icons/fi";
import { voteToResearchSpotlightTool, getResearchSpotlightToolVotes } from "../../api/Api";
import { showToast } from "../../utils/toastUtils";

const ResearchSpotLight = () => {
  const navigate = useNavigate();
  
  // State to track votes for coming soon tools
  const [toolVotes, setToolVotes] = useState({});
  const [isLoadingVotes, setIsLoadingVotes] = useState(false);


  // Fetch existing vote statuses on component mount
  useEffect(() => {
    const fetchVoteStatuses = async () => {
      try {
        setIsLoadingVotes(true);
        const response = await getResearchSpotlightToolVotes();
      
        if (response?.status === 200) {
          let votesData = response.data;
          
          if (votesData && Array.isArray(votesData)) {
            // Transform the response to match our state structure
            const votesMap = {};
            votesData.forEach(vote => {
             
              const toolId = vote.tool_id ;
              const voteValue = vote.vote !== undefined ? vote.vote : vote.vote_value;
              
              if (toolId) {
                votesMap[toolId] = voteValue === true ? "positive" : voteValue === false ? "negative" : null;
                console.log(`Mapped vote for ${toolId}:`, votesMap[toolId]);
              }
            });
            setToolVotes(votesMap);
          } else {
            console.log("No valid votes data found in response");
          }
        } else {
          console.log("Response status not 200:", response?.status);
        }
      } catch (error) {
        console.error("Failed to fetch vote statuses:", error);
      } finally {
        setIsLoadingVotes(false);
      }
    };

    fetchVoteStatuses();
  }, []);

  // Handle voting for coming soon tools
  const handleToolVote = async (voteType, toolId, feedbackMessage) => {
  
    try {
      const vote = voteType === "positive" ? true : voteType === "negative" ? false : null;
      
      // Update local state immediately for better UX
      setToolVotes(prev => {
        const newState = { ...prev, [toolId]: voteType };
        return newState;
      });
      
      // Submit vote to API
      const response = await voteToResearchSpotlightTool({
        toolId,
        vote,
        feedback: feedbackMessage,
        toolType: "coming-soon"
      });
         
      if (response?.status === 200) {
        if (vote === true || vote === false) {
          showToast("Thank you for your feedback!", "success");
        }
      } else {
        // Revert the vote if API call failed
        setToolVotes(prev => ({ ...prev, [toolId]: null }));
        showToast("Failed to submit vote", "error");
      }
    } catch (error) {
      // Revert the vote if API call failed
      setToolVotes(prev => ({ ...prev, [toolId]: null }));
      showToast("Failed to submit vote", "error");
      console.error("Voting failed", error);
    }
  };

  /// ToolCard component to display individual tool information
  const ToolCard = ({
    icon,
    title,
    description,
    buttonText = "Open Tool",
    buttonType = "external",
    isComingSoon = false,
    isPopular = false,
    onClick = () => {},
    toolId = null,
  }) => {
    console.log(`ToolCard ${toolId} - Current vote status:`, toolVotes[toolId]);
    
    return (
      <div className="card p-3 h-100">
        <div className="d-flex justify-content-between align-items-start">
          <div
            className={`rounded p-2 `}
            style={{
              color: "#0BA7FF",
              backgroundColor: "#CEEDFF",
              width: "fit-content",
            }}
          >
            {icon}
          </div>
          <div className="d-flex gap-2 ">
            {isPopular && <NoticeTag title="Popular" variant="blue" />}
            {isComingSoon && <NoticeTag title="Coming Soon" />}
          </div>
        </div>
        <h6 className="mt-3 ">{title}</h6>
        <p className="text-muted p4 ">{description}</p>

        {buttonType === "external" ? (
          <CustomButton
            onClick={onClick}
            variant="outline"
            className={`btn-sm px-3 py-2 w-100 justify-between`}
          >
            <span className="flex-grow-1 text-start">{buttonText}</span>
            <FiExternalLink size={18} />
          </CustomButton>
        ) : (
          <VoteButton
            title={buttonText}
            itemId={toolId}
            feedbackType={isLoadingVotes ? null : toolVotes[toolId]}
            onVote={handleToolVote}
            itemType="research-spotlight-tool"
            className="mt-auto"
          />
        )}
      </div>
    );
  };

  /// ToolSection component to group tool cards
  const ToolSection = ({ title, children }) => (
    <div className="mb-3">
      <p className="mb-3 h5">{title}</p>
      <div className="row g-3">
        {React.Children.map(children, (child, index) => (
          <div className="col-12 col-md-6 col-lg-4" key={index}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="container-fluid p-4" style={{ backgroundColor: "#F9FAFC" }}>
      <div className="container">
        <header className="mb-4">
          <h4 className="fw-bold">Research Spotlight</h4>
          <p className="text-muted p4 ">
            Discover our suite of Al-powered tools designed to streamline your
            research workflow and improve manuscript quality. Select any
            available tool to get started or check back soon for upcoming
            features.
          </p>
        </header>
        <ToolSection title="Research Tools">
          <ToolCard
            icon={<FiSearch size={25} />}
            title="Explore Research Papers"
            description={
              <>
                Search and explore academic papers from millions of
                publications. <strong>Powered by OpenAlex</strong>, discover
                relevant research, track citations, and stay updated with latest
                findings in your field.
              </>
            }
            onClick={() => navigate("/explore-research-papers")}
            isPopular
          />
        </ToolSection>
        <hr />
        <ToolSection title="Publishing Tools">
          <ToolCard
            icon={<TbFileCheck size={25} />}
            title="Manuscript Validator"
            description="Format your manuscript to match your target journal's submission guidelines."
            onClick={() => navigate("/manuscript-validator")}
            isPopular
          />
          <ToolCard
            icon={<PiBookBookmarkBold size={24} />}
            title="Journal Recommender"
            description="Get instant journal suggestions based on your abstract."
            onClick={() => navigate("/journal-recommender")}
            isPopular
          />
          <ToolCard
            icon={<LuCalendar size={22} />}
            title="Conference Recommender"
            description="Discover relevant conferences for your research based on your abstract."
            onClick={() => navigate("/conference-recommender")}
            isPopular
          />
          <ToolCard
            icon={<FiEdit3 size={20} />}
            title="Cover Letter Generator"
            description="Create a journal-specific cover letter using your manuscript and selected submission details."
            buttonText="Cast your vote"
            buttonType="vote"
            isComingSoon
            toolId="cover-letter-generator"
          />
        </ToolSection>
        <hr />
        <ToolSection title="Research Funding Tools">
          <ToolCard
            icon={<IoMdHelpBuoy size={26} />}
            title="Grant Application Helper"
            description="Generate tailored grant application content based on your project details, designed to align with finder requirements and boost your chances of success."
            buttonText="Cast your vote"
            buttonType="vote"
            isComingSoon
            toolId="grant-application-helper"
          />
        </ToolSection>
        <hr />
        <ToolSection title="Commercial Readiness Tools">
          <ToolCard
            icon={<FaChartLine size={21} />}
            title="Commercialization Readiness Assessment"
            description="Evaluate your project's potential for market impact with a structured assessment of IP, market fit, and development readiness."
            buttonText="Cast your vote"
            buttonType="vote"
            isComingSoon
            toolId="commercialization-readiness-assessment"
          />
        </ToolSection>
      </div>
    </div>
  );
};

export default ResearchSpotLight;
